import React, { useCallback, useEffect, useRef, useState } from "react";

import { push } from "connected-react-router";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "redux";
import { useTranslation } from "react-i18next";
import GridFullHeight from "components/GridFullHeight";
import GridFormContainer from "components/GridFormContainer";
import clsx from "clsx";
import styles from "./styles";
import Container from "@material-ui/core/Container";
import HeaderBarSub2 from "components/HeaderBarSub2";
import axios from "api/configApi";
import AwardList from "./components/AwardList";
import RankFilterBar from "./components/RankFilterBar";
import { reducerType } from "redux/reducers";
import type { LeaderboardUser, RankFilter } from "./data/types";
import { formatVolunteerTime } from "common/utils";
import { UserRole } from "common";

export type { LeaderboardUser, RankFilter } from "./data/types";

const TABS = ["ALL_TIME", "YEARLY", "MONTHLY", "WEEKLY", "DAILY"] as const;
const LIMIT = 30;

const RANK_FILTER_ROLES = [
  UserRole.Volunteer,
  UserRole.VolunteerSenior,
  UserRole.VolunteerLead,
  UserRole.Administrator,
];

const PERIOD_BY_TAB: Record<string, string> = {
  YEARLY: "yearly",
  MONTHLY: "monthly",
  WEEKLY: "weekly",
  DAILY: "daily",
};

const getQueryParams = (
  skipValue: number,
  tab: string,
  searchKeyword: string,
  limitValue: number
): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {
    limit: limitValue,
    skip: skipValue,
  };
  if (tab !== "ALL_TIME") {
    params.filter = "period";
    params.period = PERIOD_BY_TAB[tab] ?? "yearly";
  }
  const q = searchKeyword.trim();
  if (q.length > 0) {
    params.q = q;
    params.search = "name";
  }
  return params;
};

const getTotalMinutesParams = (tab: string): Record<string, string> => {
  const params: Record<string, string> = {};
  if (tab !== "ALL_TIME") {
    params.filter = "period";
    params.period = PERIOD_BY_TAB[tab] ?? "yearly";
  }
  return params;
};

const LeaderBoard = React.memo(() => {
  const dispatch = useDispatch<Dispatch>();
  const [selectedTab, setSelectedTab] = useState<(typeof TABS)[number]>(
    TABS[0]
  );
  const [rankFilter, setRankFilter] = useState<RankFilter>("TOP_RANKED");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardUser[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [myRankItemId, setMyRankItemId] = useState<string | null>(null);
  const [totalVolunteerMinutes, setTotalVolunteerMinutes] = useState<
    number | null
  >(null);

  const loadMoreRef = useRef<HTMLDivElement>(null);
  const nextSkipRef = useRef(0);

  const classes = styles();
  const translate = useTranslation().t;
  const currentUser = useSelector((state: reducerType) => state.auth.current_user);
  const canShowRankFilter =
    currentUser.isLogin && RANK_FILTER_ROLES.includes(currentUser.role);

  const selectedIndex = TABS.indexOf(selectedTab);

  /** MY_RANK requires a positive numeric rank from the API / profile */
  const hasValidLeaderboardRank =
    typeof currentUser?.rank === "number" && currentUser.rank > 0;
  const isMyRankWithoutRank =
    rankFilter === "MY_RANK" && !hasValidLeaderboardRank;

  const openUrl = (url: string) => {
    dispatch(push(url));
  };

  const handleTabChange = (idx: number) => {
    setSelectedTab(TABS[idx]);
  };

  const handleRankFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRankFilter((e.target.value as RankFilter) || "TOP_RANKED");
  };

  const handleSearch = (keyword: string) => {
    setSearchKeyword(keyword.trim());
  };

  const fetchLeaderboard = useCallback(
    async (
      reset = false,
      tabOverride?: string,
      rankFilterOverride?: RankFilter
    ) => {
      const tab = tabOverride ?? selectedTab;
      const filter = rankFilterOverride ?? rankFilter;
      const isMyRankMode = filter === "MY_RANK";

      if (isMyRankMode) {
        const r = currentUser?.rank;
        if (typeof r !== "number" || r <= 0) {
          return;
        }
      }

      if (reset) {
        nextSkipRef.current = 0;
      }
      const currentSkip = reset ? 0 : nextSkipRef.current;

      let limitForRequest = LIMIT;
      if (reset && isMyRankMode) {
        const r = currentUser?.rank;
        if (typeof r === "number" && r > 0) {
          limitForRequest = r <= LIMIT ? LIMIT : r + 20;
        }
      } else if (!reset) {
        limitForRequest = LIMIT;
      }

      try {
        if (reset) {
          setLoading(true);
          setLeaderboardData([]);
        } else {
          setLoadingMore(true);
        }

        const params = getQueryParams(
          currentSkip,
          tab,
          searchKeyword,
          limitForRequest
        );

        const res = await axios.get(`/users/leaderboard`, { params });
        const raw = Array.isArray(res.data) ? res.data : [];

        if (reset) {
          setLeaderboardData(raw);
          nextSkipRef.current = raw.length;
          if (isMyRankMode) {
            setMyRankItemId(currentUser?.id ?? null);
          } else {
            setMyRankItemId(null);
          }
        } else {
          setLeaderboardData((prev) => [...prev, ...raw]);
          nextSkipRef.current = currentSkip + raw.length;
        }
        setHasMore(raw.length >= limitForRequest);
      } catch {
        if (reset) {
          setLeaderboardData([]);
        }
        setHasMore(false);
        if (reset) setMyRankItemId(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [selectedTab, rankFilter, searchKeyword, currentUser]
  );

  const fetchTotalMinutes = useCallback(async (tabOverride?: string) => {
    const tab = tabOverride ?? selectedTab;
    setTotalVolunteerMinutes(null);
    try {
      const params = getTotalMinutesParams(tab);
      const res = await axios.get(`/users/count-leaderboard`, { params });
      const minutes = res.data?.totalVolunteerMinutes;
      setTotalVolunteerMinutes(typeof minutes === "number" ? minutes : 0);
    } catch {
      setTotalVolunteerMinutes(0);
    }
  }, [selectedTab]);

  useEffect(() => {
    if (isMyRankWithoutRank) {
      setLeaderboardData([]);
      setHasMore(false);
      setMyRankItemId(null);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    window.scrollTo(0, 0);
    fetchLeaderboard(true, selectedTab, rankFilter);
  }, [
    selectedTab,
    rankFilter,
    searchKeyword,
    fetchLeaderboard,
    isMyRankWithoutRank,
  ]);

  useEffect(() => {
    void fetchTotalMinutes(selectedTab);
  }, [selectedTab, fetchTotalMinutes]);

  const handleLoadMore = useCallback(() => {
    if (!loadingMore && hasMore) fetchLeaderboard(false);
  }, [loadingMore, hasMore, fetchLeaderboard]);

  useEffect(() => {
    if (loading || loadingMore || !hasMore || leaderboardData.length === 0) {
      return;
    }
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { root: null, rootMargin: "100px", threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [loading, loadingMore, hasMore, leaderboardData.length, handleLoadMore]);

  return (
    <GridFullHeight container>
      <GridFormContainer item xs={12} sm={12} md={12}>
        <GridFullHeight container>
          <HeaderBarSub2
            openUrl={openUrl}
            isSearch={true}
            name={translate("VOLUNTEER_LEADERBOARD")}
            handleSearch={handleSearch}
            searchPlaceholder={translate("SEARCH_NAME")}
          />

          <Container
            data-leaderboard-sticky-filters
            className={clsx(
              classes.root,
              classes.stickyFiltersBar,
            )}
          >
            <ButtonGroup
              className={classes.groupBtns}
              variant="contained"
              color="primary"
              aria-label="period tabs"
            >
              {TABS.map((tab, idx) => (
                <Button
                  key={tab}
                  onClick={() => handleTabChange(idx)}
                  className={clsx(
                    { [classes.btnActive]: idx === selectedIndex },
                    classes.btnFilter
                  )}
                >
                  {translate(tab)}
                </Button>
              ))}
            </ButtonGroup>

            {canShowRankFilter && (
              <RankFilterBar rankFilter={rankFilter} onChange={handleRankFilterChange}/>
            )}

            <div className={classes.totalVolunteerTime}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>
                {translate("TOTAL_VOLUNTEER_TIME")}:
              </Typography>
              {totalVolunteerMinutes === null ? (
                <CircularProgress size={14} />
              ) : (
                <Typography variant="body2">
                  {formatVolunteerTime(totalVolunteerMinutes, translate)}
                </Typography>
              )}
            </div>
          </Container>

          {isMyRankWithoutRank ? (
            <Container className={classes.emptyMyRankMessage}>
              <Typography variant="body1">
                {translate("LEADERBOARD_MY_RANK_NOT_RANKED")}
              </Typography>
            </Container>
          ) : (
            <AwardList
              leaderboardData={leaderboardData}
              loading={loading}
              loadingMore={loadingMore}
              hasMore={hasMore}
              myRankItemId={myRankItemId}
              rankFilter={rankFilter}
              loadMoreRef={loadMoreRef}
            />
          )}
        </GridFullHeight>
      </GridFormContainer>
    </GridFullHeight>
  );
});

export default LeaderBoard;
